/**
 * PORTAL-6207 — data-quality "creation problems" recorded when a tour was created via the lenient
 * path (BE PORTAL-5827/6203, on the shared TourDetailsDto that the /v0 orders search + details
 * endpoints serve). Codes: ASSET_NOT_FOUND, STOP_COORDINATES_UNRESOLVED, INVALID_ASSET_TIMEFRAME,
 * ASSET_NOT_AVAILABLE_FOR_SHIPPER, CARRIER_NOT_FOUND. Each problem is { code, message, fieldPath, value }.
 *
 * The BE never rewrites creation_problems after creation, so a later update can fix the data while
 * the stored problem lingers. deriveOrderAttention cross-checks each problem against the order's live
 * vehicles/stops/carrier and drops the ones that are demonstrably already resolved.
 */

/** Alphanumerics only, upper-cased — so "PL·SDS 4455", "PL SDS4455" and "SDS4455" compare equal. */
const normalizePlate = (value) => (value || '').replace(/[^a-z0-9]/gi, '').toUpperCase();

/**
 * Whether an ASSET_NOT_FOUND value matches a plate now on the tour. The BE renders the value as
 * "<CC> <number>" but may drop an empty country (the bare number), so we accept a match on either the
 * number or the country+number, both normalized.
 */
const plateResolvedNow = (vehicles, value) => {
  const target = normalizePlate(value);
  if (!target) return false;
  return (vehicles || []).some((vehicle) => {
    // Gate on a real linked vehicle id: the lenient create drops an unresolvable plate entirely
    // (the order is "created without it"), so a plate present here WITH an id is a genuine
    // resolution — never a stub of the still-unresolved plate.
    if (!vehicle || !vehicle.id) return false;
    const number = normalizePlate(vehicle.licencePlateNumber);
    const withCountry = normalizePlate(
      `${vehicle.licencePlateCountryCode || ''}${vehicle.licencePlateNumber || ''}`,
    );
    return target === number || target === withCountry;
  });
};

/**
 * Whether a STOP_COORDINATES_UNRESOLVED value (the stop's name) matches a stop that now has
 * coordinates. Only a confident name match counts as resolved — an unmatched value keeps the problem
 * visible rather than hiding a real issue. Name is the only join key available (fieldPath indexes the
 * original create request, which need not line up with the current stops).
 */
const stopResolvedNow = (stops, value) => {
  const target = (value || '').trim().toLowerCase();
  if (!target) return false;
  return (stops || []).some((stop) => {
    if (!stop) return false;
    const names = [stop.name, stop.place && stop.place.name]
      .map((name) => (name ? name.trim().toLowerCase() : name));
    const matchesName = names.some((name) => name === target);
    return matchesName && stop.latitude != null && stop.longitude != null;
  });
};

const isCreationProblemResolved = (problem, order) => {
  switch (problem.code) {
    case 'ASSET_NOT_FOUND':
      return plateResolvedNow(order.vehicles, problem.value);
    case 'STOP_COORDINATES_UNRESOLVED':
      return stopResolvedNow(order.stops, problem.value);
    case 'CARRIER_NOT_FOUND':
      // Unresolvable at creation leaves carrierCompany null (no stub); a later update that attached
      // a real (id-bearing) carrier fixes it.
      return Boolean(order.carrierCompany && order.carrierCompany.id);
    default:
      // INVALID_ASSET_TIMEFRAME / ASSET_NOT_AVAILABLE_FOR_SHIPPER and any future code have no
      // reliable client-side "resolved" signal, so they always surface until QA/the BE clears them.
      return false;
  }
};

/**
 * The tour's creation problems that still hold today (stale, already-resolved ones removed). Empty
 * when the order is clean, has no problems, or all its problems have been resolved by a later update.
 */
export const deriveOrderAttention = (order) => {
  if (!order || !Array.isArray(order.creationProblems)) return [];
  return order.creationProblems.filter(
    (problem) => problem && !isCreationProblemResolved(problem, order),
  );
};

/** i18n key for a problem code's label. */
export const creationProblemLabelKey = (code) => `ORDER.CREATION_PROBLEMS.${code}`;

/** Human label for a problem code, falling back to a generic label for unknown codes. Shared by
 * the list icon tooltip and the detail alert so the two surfaces never drift. */
export const creationProblemLabel = (t, code) => t(creationProblemLabelKey(code), {
  defaultValue: t('ORDER.CREATION_PROBLEMS.GENERIC'),
});
